import { useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Client,
  LIFECYCLE_STAGES,
  CHURN_REASONS,
  NURTURE_CADENCES,
  NurtureCadence,
  nextCheckInFor,
  isCheckInDue,
} from '@/types/crm';
import { formatDistanceToNow } from 'date-fns';
import { Archive, BellRing } from 'lucide-react';

interface ClientArchiveProps {
  clients: Client[];
  onClientClick?: (client: Client) => void;
  onSetCadence: (client: Client, cadence: NurtureCadence | null) => void;
  savingClientId?: string | null;
}

// Archived rows sort by how overdue their check-in is. Rows with no cadence set
// sink to the bottom rather than disappearing — they are still searchable, they
// are just not part of the working list.
function sortForNurture(a: Client, b: Client): number {
  const aDue = a.next_check_in_on;
  const bDue = b.next_check_in_on;
  if (aDue && bDue) return aDue.localeCompare(bDue);
  if (aDue) return -1;
  if (bDue) return 1;
  return a.company_name.localeCompare(b.company_name);
}

function endedAt(client: Client): string | undefined {
  return client.lifecycle_stage === 'paused' ? client.paused_at : client.churned_at;
}

export function ClientArchive({
  clients,
  onClientClick,
  onSetCadence,
  savingClientId,
}: ClientArchiveProps) {
  const [onlyDue, setOnlyDue] = useState(false);

  const sorted = useMemo(() => [...clients].sort(sortForNurture), [clients]);
  const dueCount = useMemo(() => clients.filter(isCheckInDue).length, [clients]);
  const rows = onlyDue ? sorted.filter(isCheckInDue) : sorted;

  if (clients.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
        <Archive className="h-8 w-8" />
        <p className="text-sm">Nothing archived yet.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setOnlyDue(!onlyDue)}
          className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors ${
            onlyDue ? 'bg-secondary border-foreground/20' : 'hover:bg-muted'
          }`}
        >
          <BellRing className="h-3.5 w-3.5" />
          Due for check-in
          <Badge variant={dueCount > 0 ? 'default' : 'outline'}>{dueCount}</Badge>
        </button>
        <span className="text-xs text-muted-foreground">
          {clients.length} archived · churned and paused relationships worth nurturing
        </span>
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Why it ended</TableHead>
              <TableHead>Ended</TableHead>
              <TableHead>Check in</TableHead>
              <TableHead>Next</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((client) => {
              const ended = endedAt(client);
              const due = isCheckInDue(client);
              return (
                <TableRow
                  key={client.id}
                  className={onClientClick ? 'cursor-pointer' : undefined}
                  onClick={onClientClick ? () => onClientClick(client) : undefined}
                >
                  <TableCell className="font-medium">{client.company_name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {client.primary_contact_name || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {LIFECYCLE_STAGES[client.lifecycle_stage].label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-[16rem] truncate">
                    {client.churn_reason
                      ? CHURN_REASONS[client.churn_reason].label
                      : client.pause_reason || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                    {ended ? formatDistanceToNow(new Date(ended), { addSuffix: true }) : '—'}
                  </TableCell>
                  {/* Cadence is the only editable cell — set it here without
                      opening the record, since the point of this view is to
                      triage 187 rows quickly. */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={client.nurture_cadence ?? 'none'}
                      disabled={savingClientId === client.id}
                      onValueChange={(v) =>
                        onSetCadence(client, v === 'none' ? null : (v as NurtureCadence))
                      }
                    >
                      <SelectTrigger size="sm" className="w-40 text-xs">
                        <SelectValue placeholder="Not nurturing" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" className="text-xs">
                          Not nurturing
                        </SelectItem>
                        {(Object.keys(NURTURE_CADENCES) as NurtureCadence[]).map((cadence) => (
                          <SelectItem key={cadence} value={cadence} className="text-xs">
                            {NURTURE_CADENCES[cadence].label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {client.next_check_in_on ? (
                      <span className={due ? 'text-warning font-medium' : 'text-muted-foreground'}>
                        {due ? 'Due now' : formatDistanceToNow(new Date(client.next_check_in_on), { addSuffix: true })}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Exported so CRMPage can compute the same next date it will display.
export { nextCheckInFor };
